<template>
  <div class="integrations-page">
    <!-- Breadcrumb -->
    <div class="breadcrumb-ig">
      <router-link to="/">Dashboard</router-link>
      <span class="crumb-sep">/</span>
      <span v-if="activeIntegration" class="crumb-link" @click="closeIntegration">Integrations</span>
      <span v-else class="crumb-current">Integrations</span>
      <template v-if="activeIntegration">
        <span class="crumb-sep">/</span>
        <span class="crumb-current">WhatsApp</span>
      </template>
    </div>

    <!-- ══════════════════════════ LANDING / CATALOG ══════════════════════════ -->
    <template v-if="!activeIntegration">
      <div class="ig-hero">
        <div class="ig-hero-text">
          <h1 class="ig-title">Integrations</h1>
          <p class="ig-subtitle">Connect third-party services to power notifications, messaging and automation across your workspace.</p>
        </div>
        <div class="ig-hero-stat">
          <span class="stat-num">{{ existingConfig && existingConfig.is_enabled ? 1 : 0 }}</span>
          <span class="stat-lbl">Connected</span>
        </div>
      </div>

      <div class="catalog-section">
        <div class="catalog-head">
          <h2 class="catalog-title">Messaging</h2>
          <span class="catalog-count">4 services</span>
        </div>

        <div class="catalog-grid">
          <!-- WhatsApp (live) -->
          <article class="app-card" tabindex="0"
                   @click="openIntegration('whatsapp')" @keydown.enter="openIntegration('whatsapp')">
            <div class="app-card-top">
              <div class="app-logo app-logo-wa">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <path d="M17.6 6.31A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-6.9 11.89L4 20l4.2-1.1a7.9 7.9 0 0 0 3.84.98h.01a7.94 7.94 0 0 0 5.55-13.57ZM12.05 18.5a6.6 6.6 0 0 1-3.36-.92l-.24-.14-2.49.65.66-2.43-.16-.25a6.59 6.59 0 1 1 12.22-3.5 6.6 6.6 0 0 1-6.63 6.59Zm3.62-4.94c-.2-.1-1.17-.58-1.35-.64s-.31-.1-.44.1-.51.64-.63.77-.23.15-.43.05a5.4 5.4 0 0 1-1.59-.98 6 6 0 0 1-1.1-1.37c-.11-.2 0-.3.09-.4s.2-.23.3-.35a1.35 1.35 0 0 0 .2-.33.37.37 0 0 0 0-.35c-.05-.1-.44-1.07-.6-1.46s-.32-.34-.44-.34h-.38a.72.72 0 0 0-.52.24 2.19 2.19 0 0 0-.68 1.63 3.8 3.8 0 0 0 .8 2.02 8.71 8.71 0 0 0 3.34 2.95c.47.2.83.32 1.11.42a2.68 2.68 0 0 0 1.23.08 2 2 0 0 0 1.32-.93 1.65 1.65 0 0 0 .11-.93c-.04-.09-.17-.14-.37-.24Z"/>
                </svg>
              </div>
              <span class="conn-pill" :class="connPillClass">
                <i class="conn-dot"></i>{{ statusLabel }}
              </span>
            </div>
            <h3 class="app-name">WhatsApp Business</h3>
            <p class="app-desc">Send bid reminders, deliverable alerts and registration OTPs to your team over WhatsApp.</p>
            <div class="app-card-foot">
              <span class="app-provider">ApnaAd · Gupshup · Twilio</span>
              <span class="app-open">
                {{ existingConfig ? 'Manage' : 'Set up' }}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </span>
            </div>
          </article>

          <!-- Email (coming soon) -->
          <article class="app-card app-card-soon">
            <div class="app-card-top">
              <div class="app-logo app-logo-email">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8">
                  <rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m3.5 7 8.5 6 8.5-6"/>
                </svg>
              </div>
              <span class="conn-pill conn-soon">Coming soon</span>
            </div>
            <h3 class="app-name">Email (SMTP)</h3>
            <p class="app-desc">Deliver reminders and reports over your own SMTP server or an email provider.</p>
            <div class="app-card-foot">
              <span class="app-provider">SMTP · SendGrid · SES</span>
              <span class="app-open app-open-muted">Soon</span>
            </div>
          </article>

          <!-- SMS (coming soon) -->
          <article class="app-card app-card-soon">
            <div class="app-card-top">
              <div class="app-logo app-logo-sms">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
              </div>
              <span class="conn-pill conn-soon">Coming soon</span>
            </div>
            <h3 class="app-name">SMS Gateway</h3>
            <p class="app-desc">Fallback text alerts and OTP delivery for users without WhatsApp.</p>
            <div class="app-card-foot">
              <span class="app-provider">Twilio · MSG91 · Kaleyra</span>
              <span class="app-open app-open-muted">Soon</span>
            </div>
          </article>

          <!-- Slack (coming soon) -->
          <article class="app-card app-card-soon">
            <div class="app-card-top">
              <div class="app-logo app-logo-slack">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M5.04 15.16a2.02 2.02 0 1 1-2.02-2.02h2.02v2.02Zm1.01 0a2.02 2.02 0 0 1 4.04 0v5.06a2.02 2.02 0 1 1-4.04 0v-5.06Zm2.02-8.1a2.02 2.02 0 1 1 2.02-2.02v2.02H8.07Zm0 1.01a2.02 2.02 0 0 1 0 4.04H2.99a2.02 2.02 0 0 1 0-4.04h5.08Zm8.09 2.02a2.02 2.02 0 1 1 2.02 2.02h-2.02v-2.02Zm-1.01 0a2.02 2.02 0 0 1-4.04 0V5.03a2.02 2.02 0 1 1 4.04 0v5.06Zm-2.02 8.09a2.02 2.02 0 1 1-2.02 2.02v-2.02h2.02Zm0-1.01a2.02 2.02 0 0 1 0-4.04h5.08a2.02 2.02 0 0 1 0 4.04h-5.08Z"/>
                </svg>
              </div>
              <span class="conn-pill conn-soon">Coming soon</span>
            </div>
            <h3 class="app-name">Slack</h3>
            <p class="app-desc">Push bid activity and system alerts into your team's Slack channels.</p>
            <div class="app-card-foot">
              <span class="app-provider">Incoming Webhooks</span>
              <span class="app-open app-open-muted">Soon</span>
            </div>
          </article>
        </div>
      </div>
    </template>

    <!-- ══════════════════════════ DETAIL / SETTINGS ══════════════════════════ -->
    <template v-else>
      <div class="detail-hero">
        <button class="back-btn" @click="closeIntegration" title="Back to Integrations">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
        </button>
        <div class="app-logo app-logo-wa detail-logo">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
            <path d="M17.6 6.31A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-6.9 11.89L4 20l4.2-1.1a7.9 7.9 0 0 0 3.84.98h.01a7.94 7.94 0 0 0 5.55-13.57ZM12.05 18.5a6.6 6.6 0 0 1-3.36-.92l-.24-.14-2.49.65.66-2.43-.16-.25a6.59 6.59 0 1 1 12.22-3.5 6.6 6.6 0 0 1-6.63 6.59Zm3.62-4.94c-.2-.1-1.17-.58-1.35-.64s-.31-.1-.44.1-.51.64-.63.77-.23.15-.43.05a5.4 5.4 0 0 1-1.59-.98 6 6 0 0 1-1.1-1.37c-.11-.2 0-.3.09-.4s.2-.23.3-.35a1.35 1.35 0 0 0 .2-.33.37.37 0 0 0 0-.35c-.05-.1-.44-1.07-.6-1.46s-.32-.34-.44-.34h-.38a.72.72 0 0 0-.52.24 2.19 2.19 0 0 0-.68 1.63 3.8 3.8 0 0 0 .8 2.02 8.71 8.71 0 0 0 3.34 2.95c.47.2.83.32 1.11.42a2.68 2.68 0 0 0 1.23.08 2 2 0 0 0 1.32-.93 1.65 1.65 0 0 0 .11-.93c-.04-.09-.17-.14-.37-.24Z"/>
          </svg>
        </div>
        <div class="detail-hero-text">
          <div class="detail-title-row">
            <h1 class="detail-title">WhatsApp Business</h1>
            <span class="conn-pill" :class="connPillClass"><i class="conn-dot"></i>{{ statusLabel }}</span>
          </div>
          <p class="detail-sub">Connect any WhatsApp Business API provider (ApnaAd, Gupshup, Twilio) via a token.</p>
        </div>
        <a v-if="metaManagerUrl" :href="metaManagerUrl" target="_blank" rel="noopener" class="detail-hero-action">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
          Manage on Meta
        </a>
      </div>

      <!-- ─── Tab bar ─── -->
      <div class="tab-bar">
        <button class="tab-btn" :class="{ active: activeTab === 'config' }" @click="activeTab = 'config'">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          Configuration
        </button>
        <button class="tab-btn" :class="{ active: activeTab === 'templates' }" @click="activeTab = 'templates'">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
          </svg>
          Templates
          <span v-if="templates.length" class="tab-count">{{ templates.length }}</span>
        </button>
      </div>

      <!-- ══════ TAB: Configuration ══════ -->
      <div v-show="activeTab === 'config'" class="tab-panel config-layout">
    <div class="config-main">
      <!-- Provider switcher -->
      <div class="provider-switch">
        <div class="provider-tab" :class="{ sel: selectedProvider === 'business_api' }"
             @click="selectProvider('business_api')">
          <div class="provider-tab-head">
            <span class="provider-role">Primary</span>
            <span class="provider-onoff" :class="{ on: activeProvider === 'business_api' }">
              {{ activeProvider === 'business_api' ? 'ON' : 'OFF' }}
            </span>
          </div>
          <h4 class="provider-name">WhatsApp Business API</h4>
          <p class="provider-desc">Official Meta panel (session + API key)</p>
        </div>

        <div class="provider-tab" :class="{ sel: selectedProvider === 'apnaad' }"
             @click="selectProvider('apnaad')">
          <div class="provider-tab-head">
            <span class="provider-role">Secondary</span>
            <span class="provider-onoff" :class="{ on: activeProvider === 'apnaad' }">
              {{ activeProvider === 'apnaad' ? 'ON' : 'OFF' }}
            </span>
          </div>
          <h4 class="provider-name">ApnaAd</h4>
          <p class="provider-desc">Legacy provider (vendor UID + token)</p>
        </div>
      </div>

    <div class="integration-card">
      <div class="card-body">
        <div v-if="isLoading" class="loading-state">
          <div class="spinner-lg"></div>
          <p>Loading configuration...</p>
        </div>

        <template v-else>
          <!-- Activate banner -->
          <div class="activate-row" :class="{ active: activeProvider === selectedProvider }">
            <div class="activate-text">
              <strong>{{ activeProvider === selectedProvider ? 'This provider is live' : 'This provider is off' }}</strong>
              <span>{{ activeProvider === selectedProvider
                ? 'Reminders and OTPs are being sent through it.'
                : 'Turn it on to route all WhatsApp sends through this provider.' }}</span>
            </div>
            <button type="button" class="btn-activate"
                    :disabled="isActivating || !existingConfig || activeProvider === selectedProvider"
                    @click="activateProvider(selectedProvider)">
              <span v-if="isActivating" class="spinner-sm"></span>
              {{ activeProvider === selectedProvider ? 'Active' : 'Turn on' }}
            </button>
          </div>

          <!-- Required fields -->
          <div class="section">
            <h3 class="section-title">Required Configuration</h3>

            <div class="form-grid">
              <div class="form-field">
                <label class="form-label">API Base URL <span class="req">*</span></label>
                <input v-model="form.api_base_url" type="text" class="form-input"
                       :placeholder="isBusinessApi ? 'e.g., https://www.wa.apnaad.in' : 'e.g., https://api.provider.com/v1'" />
                <span class="form-hint">Provider's API base URL (no trailing slash)</span>
              </div>

              <!-- Business API: Session ID (auto-resolved) -->
              <div v-if="isBusinessApi" class="form-field">
                <label class="form-label">Session ID</label>
                <input v-model="form.session_id" type="text" class="form-input"
                       placeholder="Auto-detected on save — leave blank" />
                <span class="form-hint">Optional. Left blank, it's fetched from your account automatically. Paste one only to override.</span>
              </div>

              <!-- ApnaAd: Vendor UID -->
              <div v-else class="form-field">
                <label class="form-label">Vendor UID / Account ID <span class="req">*</span></label>
                <input v-model="form.vendor_uid" type="text" class="form-input"
                       placeholder="e.g., abc123-def456-ghi789" />
                <span class="form-hint">Unique vendor identifier issued by the provider</span>
              </div>

              <div class="form-field form-field-full">
                <label class="form-label">{{ isBusinessApi ? 'API Key' : 'API Token' }} <span class="req">*</span></label>
                <div class="token-wrapper">
                  <input v-model="form.api_token" :type="showToken ? 'text' : 'password'"
                         class="form-input" :placeholder="existingConfig ? 'Leave blank to keep existing key' : (isBusinessApi ? 'Paste your panel API key' : 'Paste your secret API token')" />
                  <button type="button" class="token-toggle" @click="showToken = !showToken"
                          :title="showToken ? 'Hide' : 'Show'">
                    <svg v-if="!showToken" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  </button>
                </div>
                <span v-if="existingConfig" class="form-hint">Current: <code>{{ existingConfig.api_token_masked }}</code></span>
                <span v-else class="form-hint">Keep this secret — it will be masked when reloaded.</span>
              </div>

              <div class="form-field form-field-full">
                <label class="form-label">WhatsApp Business Account ID (WABA ID)</label>
                <input v-model="form.waba_id" type="text" class="form-input"
                       placeholder="e.g., 113885025089335" />
                <span class="form-hint">Enables the "Manage on Meta" button to open the Meta WhatsApp Manager directly.</span>
              </div>
            </div>
          </div>

          <!-- Advanced Settings (ApnaAd only — Business API uses fixed endpoints) -->
          <div v-if="!isBusinessApi" class="section">
            <button type="button" class="advanced-toggle" @click="showAdvanced = !showAdvanced">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   :style="{ transform: showAdvanced ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
              Advanced Settings
            </button>

            <div v-if="showAdvanced" class="advanced-content">
              <div class="form-grid">
                <div class="form-field">
                  <label class="form-label">Provider Name</label>
                  <input v-model="form.provider_name" type="text" class="form-input" placeholder="e.g., Twilio or Gupshup" />
                  <span class="form-hint">Display label only</span>
                </div>

                <div class="form-field">
                  <label class="form-label">Send Endpoint Path</label>
                  <input v-model="form.send_endpoint" type="text" class="form-input"
                         placeholder="e.g., /api/v1/send or /messages/send" />
                  <span class="form-hint"><code>{{ '{vendor_uid}' }}</code> is replaced at runtime</span>
                </div>

                <div class="form-field">
                  <label class="form-label">Phone Field Name</label>
                  <input v-model="form.phone_field_name" type="text" class="form-input" placeholder="e.g., to or recipient_phone" />
                  <span class="form-hint">JSON key in request body</span>
                </div>

                <div class="form-field">
                  <label class="form-label">Message Field Name</label>
                  <input v-model="form.message_field_name" type="text" class="form-input" placeholder="e.g., to or recipient_phone" />
                  <span class="form-hint">JSON key in request body</span>
                </div>

                <div class="form-field form-field-full">
                  <label class="form-label">Webhook URL (Incoming)</label>
                  <input v-model="form.webhook_url" type="text" class="form-input"
                         placeholder="e.g., https://app.example.com/webhooks/messages" />
                  <span class="form-hint">Paste this URL in your provider's webhook settings to receive incoming messages</span>
                </div>

                <div class="form-field form-field-full">
                  <label class="form-label">Status</label>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="form.is_enabled" />
                    <span class="toggle-slider"></span>
                    <span class="toggle-label">{{ form.is_enabled ? 'Enabled' : 'Disabled' }}</span>
                  </label>
                  <span class="form-hint">Toggle to disable integration without removing config</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="saveMessage" class="alert" :class="saveSuccess ? 'alert-success' : 'alert-error'">
            {{ saveMessage }}
          </div>

          <!-- Actions -->
          <div class="actions-row">
            <button type="button" class="btn-primary" :disabled="isSaving" @click="save">
              <span v-if="isSaving" class="spinner-sm"></span>
              {{ isSaving ? 'Saving...' : 'Save Configuration' }}
            </button>
          </div>
        </template>
      </div>
    </div>
    </div>

    <!-- Right: connection + tests -->
    <aside class="config-guide">
      <div class="side-card">
        <h4 class="side-title">Connection</h4>
        <div class="side-status">
          <span class="conn-pill" :class="connPillClass"><i class="conn-dot"></i>{{ statusLabel }}</span>
        </div>
        <p class="side-note" v-if="!existingConfig">This provider isn't configured yet. Fill in the fields and save.</p>
        <p class="side-note" v-else-if="activeProvider === selectedProvider">This provider is live. Reminders and OTPs go through it.</p>
        <p class="side-note" v-else>Saved but off. Turn it on above to route sends through this provider.</p>
        <a v-if="metaManagerUrl" :href="metaManagerUrl" target="_blank" rel="noopener" class="side-link">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
          Open Meta WhatsApp Manager
        </a>
      </div>

      <div class="side-card">
        <h4 class="side-title">Test delivery</h4>
        <p class="side-note">Send a live message to a number to verify the setup.</p>
        <button type="button" class="side-btn" :disabled="!existingConfig || isTesting" @click="openTest">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          Send Test Message
        </button>
        <button type="button" class="side-btn" :disabled="!existingConfig || isTesting" @click="openTestTemplate">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
          </svg>
          Send Test Template
        </button>
        <button type="button" class="side-btn" :disabled="!existingConfig || isTesting" @click="openTestOtp">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
          </svg>
          Send Test OTP
        </button>
      </div>
    </aside>
      </div><!-- /tab config -->

      <!-- ══════ TAB: Templates ══════ -->
      <div v-show="activeTab === 'templates'" class="tab-panel">

    <!-- Template usage mapping -->
    <div class="integration-card" style="margin-bottom: 1.5rem;">
      <div class="card-header">
        <div class="card-title-row">
          <div class="card-icon" style="background:#0ea5e9;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M4 4h16v4H4zM4 12h10v4H4zM4 20h6"/>
            </svg>
          </div>
          <div>
            <h2 class="card-title">Template usage</h2>
            <p class="card-subtitle">Choose which template drives each automated message.</p>
          </div>
        </div>
        <button type="button" class="btn-solid" :disabled="isSavingUsage || !templates.length" @click="saveTemplateUsage">
          <span v-if="isSavingUsage" class="spinner-sm"></span>
          <span>{{ isSavingUsage ? 'Saving…' : 'Save usage' }}</span>
        </button>
      </div>
      <div class="card-body">
        <div v-if="!templates.length" class="placeholder-box">
          <p>Sync your templates first, then map them to reminders and OTP.</p>
        </div>
        <template v-else>
          <div class="usage-grid">
            <div v-for="row in usageRows" :key="row.purpose" class="usage-row">
              <div class="usage-label">
                <strong>{{ row.label }}</strong>
                <span>{{ row.hint }}</span>
              </div>
              <select v-model="row.template_name" class="form-input usage-select" @change="onUsageChange(row)">
                <option value="">— Not set —</option>
                <option v-for="tpl in templatesForPurpose(row.purpose)" :key="tpl.id" :value="tpl.name">
                  {{ tpl.name }} ({{ tpl.language }})
                </option>
              </select>
            </div>
          </div>
          <div v-if="usageMessage" class="alert" :class="usageSuccess ? 'alert-success' : 'alert-error'" style="margin-top: 1rem;">
            {{ usageMessage }}
          </div>
        </template>
      </div>
    </div>

    <!-- Templates Card -->
    <div class="integration-card">
      <div class="card-header">
        <div class="card-title-row">
          <div class="card-icon" style="background:#6366f1;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
            </svg>
          </div>
          <div>
            <h2 class="card-title">Message Templates</h2>
            <p class="card-subtitle">Templates synced from your WhatsApp provider (Meta). Create new ones or sync the latest.</p>
          </div>
        </div>
        <div class="card-header-actions">
          <a v-if="metaManagerUrl" :href="metaManagerUrl" target="_blank" rel="noopener" class="btn-outline">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            <span>Manage on Meta</span>
          </a>
          <button type="button" class="btn-outline" :disabled="isSyncingTemplates || !existingConfig" @click="syncTemplates">
            <span v-if="isSyncingTemplates" class="spinner-sm dark"></span>
            <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            <span>{{ isSyncingTemplates ? 'Syncing…' : 'Sync' }}</span>
          </button>
          <button type="button" class="btn-solid" :disabled="!existingConfig" @click="openCreateTemplate">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Create</span>
          </button>
        </div>
      </div>
      <div class="card-body">
        <div v-if="!existingConfig" class="placeholder-box">
          <p>Save the configuration first, then sync your approved templates here.</p>
        </div>

        <div v-else-if="isLoadingTemplates" class="loading-state">
          <div class="spinner-lg"></div>
          <p>Loading templates...</p>
        </div>

        <div v-else-if="templatesError" class="alert alert-error">
          {{ templatesError }}
        </div>

        <div v-else-if="!templates.length" class="placeholder-box">
          <p>No templates cached yet.</p>
          <p class="muted">Click "Sync" to pull your approved templates from the provider.</p>
        </div>

        <template v-else>
          <div class="templates-toolbar">
            <h3 class="section-title" style="margin:0;">Synced Templates</h3>
            <span class="count-pill">{{ templates.length }}</span>
            <span class="toolbar-hint">Click a row to preview</span>
          </div>

          <div class="tpl-table-wrap">
            <table class="tpl-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Language</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Updated On</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="tpl in templates" :key="tpl.id" class="tpl-row" @click="openPreview(tpl)">
                  <td class="td-name"><code>{{ tpl.name }}</code></td>
                  <td>{{ tpl.language }}</td>
                  <td>{{ tpl.category || '—' }}</td>
                  <td>
                    <span v-if="tpl.status" class="status-pill" :class="statusClass(tpl.status)">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                      {{ tpl.status }}
                    </span>
                    <span v-else>—</span>
                  </td>
                  <td class="td-date">{{ formatDate(tpl.updated_at) || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
      </div><!-- /tab templates -->
    </template>

    <!-- Template Preview Modal -->
    <div v-if="showPreviewModal" class="modal-overlay">
      <div class="modal-backdrop" @click="closePreview"></div>
      <div class="modal-panel">
        <div class="modal-header">
          <h2 class="modal-title">Template Preview</h2>
          <button class="modal-close-btn" @click="closePreview">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="modal-body" v-if="previewTpl">
          <div class="preview-meta-row">
            <code class="preview-name">{{ previewTpl.name }}</code>
            <span v-if="previewTpl.status" class="status-pill" :class="statusClass(previewTpl.status)">{{ previewTpl.status }}</span>
          </div>
          <div class="preview-meta-tags">
            <span v-if="previewTpl.category" class="meta-chip">{{ previewTpl.category }}</span>
            <span class="meta-chip">{{ previewTpl.language }}</span>
          </div>

          <!-- WhatsApp-style bubble -->
          <div class="wa-preview">
            <div class="wa-bubble">
              <div v-if="previewTpl.header_text" class="wa-header">{{ previewTpl.header_text }}</div>
              <div v-if="previewTpl.body_text" class="wa-body">{{ previewTpl.body_text }}</div>
              <div v-else class="wa-body wa-empty">No body content available for this template.</div>
              <div v-if="previewTpl.footer_text" class="wa-footer">{{ previewTpl.footer_text }}</div>
              <div class="wa-time">{{ nowTime }}</div>
            </div>
            <div v-if="previewTpl.buttons && previewTpl.buttons.length" class="wa-buttons">
              <div v-for="(b, i) in previewTpl.buttons" :key="i" class="wa-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
                {{ b.text || b.type }}
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" @click="closePreview">Close</button>
        </div>
      </div>
    </div>

    <!-- Create Template Modal -->
    <div v-if="showCreateModal" class="modal-overlay">
      <div class="modal-backdrop" @click="closeCreateTemplate"></div>
      <div class="modal-panel modal-wide">
        <div class="modal-header">
          <h2 class="modal-title">Create Template</h2>
          <button class="modal-close-btn" @click="closeCreateTemplate">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label">Template Name <span class="req">*</span></label>
              <input v-model="createForm.template_name" type="text" class="form-input" placeholder="e.g., order_ready" />
              <span class="form-hint">Lowercase, underscores only — must match Meta naming rules</span>
            </div>
            <div class="form-field">
              <label class="form-label">Category <span class="req">*</span></label>
              <select v-model="createForm.category" class="form-input">
                <option value="UTILITY">UTILITY</option>
                <option value="MARKETING">MARKETING</option>
              </select>
              <span class="form-hint">AUTHENTICATION must be created on Meta directly</span>
            </div>
            <div class="form-field">
              <label class="form-label">Language Code <span class="req">*</span></label>
              <input v-model="createForm.language_code" type="text" class="form-input" placeholder="en_US" />
            </div>
            <div class="form-field">
              <label class="form-label">Template Type</label>
              <input v-model="createForm.template_type" type="text" class="form-input" placeholder="header" />
            </div>
            <div class="form-field form-field-full">
              <label class="form-label">Body Text <span class="req">*</span></label>
              <textarea v-model="createForm.template_body" rows="3" class="form-input"
                        :placeholder="bodyPlaceholder"></textarea>
              <span class="form-hint">Use <code>{{ var1 }}</code>, <code>{{ var2 }}</code> for variables</span>
            </div>
            <div class="form-field form-field-full">
              <label class="form-label">Footer Text</label>
              <input v-model="createForm.template_footer" type="text" class="form-input" placeholder="Your Business Name" />
            </div>
            <div class="form-field form-field-full" v-if="detectedVars.length">
              <label class="form-label">Example Variable Values</label>
              <span class="form-hint" style="margin-top:0; margin-bottom:0.5rem;">
                Sample value for each variable (helps Meta review). Detected {{ detectedVars.length }} variable(s).
              </span>
              <div class="var-grid">
                <div v-for="n in detectedVars" :key="n" class="var-field">
                  <span class="var-tag">{{ varLabel(n) }}</span>
                  <input v-model="exampleValues[n]" type="text" class="form-input" :placeholder="'Sample for ' + varLabel(n)" />
                </div>
              </div>
            </div>
          </div>
          <div v-if="createMessage" class="alert" :class="createSuccess ? 'alert-success' : 'alert-error'">
            {{ createMessage }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" :disabled="isCreating" @click="closeCreateTemplate">Cancel</button>
          <button type="button" class="btn-primary whatsapp" :disabled="isCreating || !createForm.template_name || !createForm.template_body" @click="submitCreateTemplate">
            <span v-if="isCreating" class="spinner-sm"></span>
            {{ isCreating ? 'Submitting...' : 'Submit for Review' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Test Template Modal -->
    <div v-if="showTestTemplateModal" class="modal-overlay">
      <div class="modal-backdrop" @click="closeTestTemplate"></div>
      <div class="modal-panel">
        <div class="modal-header">
          <h2 class="modal-title">Send Test Template</h2>
          <button class="modal-close-btn" @click="closeTestTemplate">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-field" style="margin-bottom: 1rem;">
            <label class="form-label">Phone Number (with country code)</label>
            <input v-model="testTemplateForm.phone_number" type="text" class="form-input" placeholder="919876543210" />
          </div>

          <div class="form-field" style="margin-bottom: 1rem;">
            <label class="form-label">Template <span class="req">*</span></label>
            <select v-model="testTemplateForm.template_name" class="form-input" @change="onTestTemplateChange">
              <option value="" disabled>Select a template…</option>
              <option v-for="tpl in templates" :key="tpl.id" :value="tpl.name">
                {{ tpl.name }} ({{ tpl.language }})
              </option>
            </select>
            <span v-if="!templates.length" class="form-hint">No templates synced yet. Go to the Templates tab and click Sync.</span>
          </div>

          <!-- Auto-generated variable inputs -->
          <div v-if="selectedTestTemplate && testTemplateVars.length" class="form-field" style="margin-bottom: 1rem;">
            <label class="form-label">Variables</label>
            <div class="var-grid">
              <div v-for="n in testTemplateVars" :key="n" class="var-field">
                <span class="var-tag">{{ varLabel(n) }}</span>
                <input v-model="testTemplateForm['field_' + n]" type="text" class="form-input"
                       :placeholder="'Value for ' + varLabel(n)" />
              </div>
            </div>
          </div>
          <p v-else-if="selectedTestTemplate" class="form-hint" style="margin-bottom: 1rem;">
            This template has no variables — just hit Send.
          </p>

          <!-- Live preview -->
          <div v-if="selectedTestTemplate" class="wa-preview" style="margin-bottom: 1rem;">
            <div class="wa-bubble">
              <div v-if="selectedTestTemplate.header_text" class="wa-header">{{ renderTestPreview(selectedTestTemplate.header_text) }}</div>
              <div v-if="selectedTestTemplate.body_text" class="wa-body">{{ renderTestPreview(selectedTestTemplate.body_text) }}</div>
              <div v-if="selectedTestTemplate.footer_text" class="wa-footer">{{ selectedTestTemplate.footer_text }}</div>
              <div class="wa-time">{{ nowTime }}</div>
            </div>
            <div v-if="selectedTestTemplate.buttons && selectedTestTemplate.buttons.length" class="wa-buttons">
              <div v-for="(b, i) in selectedTestTemplate.buttons" :key="i" class="wa-btn">{{ b.text || b.type }}</div>
            </div>
          </div>

          <div v-if="testTemplateMessage" class="alert" :class="testTemplateSuccess ? 'alert-success' : 'alert-error'">
            {{ testTemplateMessage }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" :disabled="isTesting" @click="closeTestTemplate">Cancel</button>
          <button type="button" class="btn-primary whatsapp" :disabled="isTesting || !testTemplateForm.phone_number || !testTemplateForm.template_name" @click="sendTestTemplate">
            <span v-if="isTesting" class="spinner-sm"></span>
            {{ isTesting ? 'Sending...' : 'Send Template' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Test OTP Modal -->
    <div v-if="showTestOtpModal" class="modal-overlay">
      <div class="modal-backdrop" @click="closeTestOtp"></div>
      <div class="modal-panel">
        <div class="modal-header">
          <h2 class="modal-title">Send Test OTP</h2>
          <button class="modal-close-btn" @click="closeTestOtp">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-field" style="margin-bottom: 1rem;">
            <label class="form-label">Phone Number (with country code) <span class="req">*</span></label>
            <input v-model="testOtpForm.phone_number" type="text" class="form-input" placeholder="919876543210" />
          </div>
          <div v-if="testOtpMessage" class="alert" :class="testOtpSuccess ? 'alert-success' : 'alert-error'">
            {{ testOtpMessage }}
          </div>
          <div v-if="testOtpMessage && testOtpSuccess && generatedOtp" class="otp-display" style="background: #f0fdf4; border: 1px solid #86efac; padding: 1rem; border-radius: 8px; margin-top: 1rem;">
            <p style="margin: 0; font-size: 0.9rem; color: #4b5563; font-weight: 600;">Generated OTP (for testing):</p>
            <p style="margin: 0.5rem 0 0 0; font-size: 1.5rem; color: #16a34a; font-family: monospace; letter-spacing: 0.2em; font-weight: 700;">{{ generatedOtp }}</p>
            <p style="margin: 0.5rem 0 0 0; font-size: 0.85rem; color: #6b7280;">Check your WhatsApp to verify the OTP was sent correctly.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" :disabled="isTesting" @click="closeTestOtp">Cancel</button>
          <button type="button" class="btn-primary whatsapp" :disabled="isTesting || !testOtpForm.phone_number" @click="sendTestOtp">
            <span v-if="isTesting" class="spinner-sm"></span>
            {{ isTesting ? 'Sending...' : 'Send OTP' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Test Modal -->
    <div v-if="showTestModal" class="modal-overlay">
      <div class="modal-backdrop" @click="closeTest"></div>
      <div class="modal-panel">
        <div class="modal-header">
          <h2 class="modal-title">Send Test Message</h2>
          <button class="modal-close-btn" @click="closeTest">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-field" style="margin-bottom: 1rem;">
            <label class="form-label">Phone Number (with country code)</label>
            <input v-model="testForm.phone_number" type="text" class="form-input" placeholder="919876543210" />
          </div>
          <div class="form-field" style="margin-bottom: 1rem;">
            <label class="form-label">Test Message</label>
            <textarea v-model="testForm.message" rows="3" class="form-input"></textarea>
          </div>
          <div v-if="testMessage" class="alert" :class="testSuccess ? 'alert-success' : 'alert-error'">
            {{ testMessage }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" :disabled="isTesting" @click="closeTest">Cancel</button>
          <button type="button" class="btn-primary whatsapp" :disabled="isTesting || !testForm.phone_number" @click="sendTest">
            <span v-if="isTesting" class="spinner-sm"></span>
            {{ isTesting ? 'Sending...' : 'Send Test' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'IntegrationsPage',
  data() {
    return {
      activeIntegration: null, // null = grid view; 'whatsapp' = detail view
      activeTab: 'config',     // config | templates | testing
      selectedProvider: 'business_api', // which provider's form is shown
      activeProvider: null,             // which provider is live (from backend)
      configs: { business_api: null, apnaad: null }, // saved config per provider
      isActivating: false,
      form: {
        provider_name: '',
        api_base_url: '',
        api_token: '',
        vendor_uid: '',
        session_id: '',
        waba_id: '',
        send_endpoint: '/{vendor_uid}/contact/send-message',
        phone_field_name: 'phone_number',
        message_field_name: 'message_body',
        webhook_url: '',
        is_enabled: true
      },
      existingConfig: null,
      showToken: false,
      showAdvanced: false,
      isLoading: false,
      isSaving: false,
      isTesting: false,
      saveMessage: '',
      saveSuccess: false,
      showTestModal: false,
      testForm: {
        phone_number: '',
        message: 'Test message from Intelvo Super Admin Panel. If you received this, your WhatsApp integration is working correctly.'
      },
      testMessage: '',
      testSuccess: false,
      showTestTemplateModal: false,
      testTemplateForm: {
        phone_number: '916200554124',
        template_name: '',
        template_language: 'en'
      },
      testTemplateMessage: '',
      testTemplateSuccess: false,
      showTestOtpModal: false,
      testOtpForm: {
        phone_number: ''
      },
      testOtpMessage: '',
      testOtpSuccess: false,
      generatedOtp: '',
      templates: [],
      isLoadingTemplates: false,
      templatesError: '',
      isSyncingTemplates: false,
      usageRows: [
        { purpose: 'reminder',    label: 'Bid reminder',        hint: 'Sent before a bid submission deadline.', template_name: '', language: 'en' },
        { purpose: 'deliverable', label: 'Deliverable reminder', hint: 'Sent for pending deliverables.',        template_name: '', language: 'en' },
        { purpose: 'otp',         label: 'Registration OTP',     hint: 'Only AUTHENTICATION-category templates.', template_name: '', language: 'en' }
      ],
      isSavingUsage: false,
      usageMessage: '',
      usageSuccess: false,
      showPreviewModal: false,
      previewTpl: null,
      var1: '{{1}}',
      var2: '{{2}}',
      bodyPlaceholder: 'Hi {{1}}, your order #{{2}} is ready!',
      showCreateModal: false,
      isCreating: false,
      createMessage: '',
      createSuccess: false,
      createForm: {
        template_name: '',
        category: 'UTILITY',
        language_code: 'en_US',
        template_type: 'header',
        template_body: '',
        template_footer: ''
      },
      exampleValues: {}
    };
  },
  computed: {
    statusLabel() {
      // Card/badge on the catalog + detail reflect whether ANY provider is live.
      if (!this.activeProvider) return 'Not configured';
      return 'Connected';
    },
    statusBadgeClass() {
      return this.activeProvider ? 'success' : 'neutral';
    },
    connPillClass() {
      // Sidebar pill reflects the CURRENTLY SELECTED provider's state.
      if (!this.existingConfig) return 'conn-off';
      return this.activeProvider === this.selectedProvider ? 'conn-on' : 'conn-warn';
    },
    nowTime() {
      return new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },
    detectedVars() {
      const matches = (this.createForm.template_body || '').match(/\{\{\s*(\d+)\s*\}\}/g) || [];
      const nums = matches.map(m => parseInt(m.replace(/[^\d]/g, ''), 10));
      return [...new Set(nums)].sort((a, b) => a - b);
    },
    selectedTestTemplate() {
      if (!this.testTemplateForm.template_name) return null;
      return this.templates.find(t => t.name === this.testTemplateForm.template_name) || null;
    },
    testTemplateVars() {
      // Collect {{n}} variables from the picked template's header + body + button texts.
      const tpl = this.selectedTestTemplate;
      if (!tpl) return [];
      const text = [tpl.header_text, tpl.body_text, tpl.footer_text,
        ...((tpl.buttons || []).map(b => (b.text || '') + ' ' + (b.url || '')))].join(' ');
      const matches = text.match(/\{\{\s*(\d+)\s*\}\}/g) || [];
      const nums = matches.map(m => parseInt(m.replace(/[^\d]/g, ''), 10));
      return [...new Set(nums)].sort((a, b) => a - b);
    },
    metaManagerUrl() {
      const waba = this.existingConfig && this.existingConfig.waba_id;
      if (!waba) return '';
      return `https://business.facebook.com/latest/whatsapp_manager/message_templates?asset_id=${encodeURIComponent(waba)}`;
    },
    isBusinessApi() {
      return this.selectedProvider === 'business_api';
    }
  },
  methods: {
    openIntegration(key) {
      this.activeIntegration = key;
      this.activeTab = 'config';
    },
    closeIntegration() {
      this.activeIntegration = null;
    },
    authHeaders() {
      const token = localStorage.getItem('super_admin_token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    async loadConfig() {
      this.isLoading = true;
      try {
        const res = await axios.get(`${this.$API_URL}/whatsapp/integrations`, {
          headers: this.authHeaders()
        });
        const data = res.data || {};
        this.configs = {
          business_api: data.business_api || null,
          apnaad: data.apnaad || null
        };
        this.activeProvider = data.active_provider || null;
        // Default the visible form to the active provider (or Business API primary).
        this.selectedProvider = this.activeProvider || 'business_api';
        this.applyFormFor(this.selectedProvider);
      } catch (err) {
        console.error('Failed to load config:', err);
      } finally {
        this.isLoading = false;
      }
    },
    applyFormFor(kind) {
      const cfg = this.configs[kind];
      this.existingConfig = cfg || null;
      this.showToken = false;
      if (cfg) {
        this.form = {
          provider_name: cfg.provider_name || '',
          api_base_url: cfg.api_base_url || '',
          api_token: '',
          vendor_uid: cfg.vendor_uid || '',
          session_id: cfg.session_id || '',
          waba_id: cfg.waba_id || '',
          send_endpoint: cfg.send_endpoint || '/{vendor_uid}/contact/send-message',
          phone_field_name: cfg.phone_field_name || 'phone_number',
          message_field_name: cfg.message_field_name || 'message_body',
          webhook_url: cfg.webhook_url || '',
          is_enabled: cfg.is_enabled
        };
      } else {
        this.form = {
          provider_name: '', api_base_url: '', api_token: '', vendor_uid: '',
          session_id: '', waba_id: '',
          send_endpoint: '/{vendor_uid}/contact/send-message',
          phone_field_name: 'phone_number', message_field_name: 'message_body',
          webhook_url: '', is_enabled: true
        };
      }
    },
    selectProvider(kind) {
      if (this.selectedProvider === kind) return;
      this.selectedProvider = kind;
      this.saveMessage = '';
      this.applyFormFor(kind);
    },
    async activateProvider(kind) {
      this.isActivating = true;
      this.saveMessage = '';
      try {
        await axios.post(`${this.$API_URL}/whatsapp/integration/activate`, { provider_kind: kind }, {
          headers: { ...this.authHeaders(), 'Content-Type': 'application/json' }
        });
        this.activeProvider = kind;
        this.saveMessage = 'Provider switched on. All WhatsApp sends now use it.';
        this.saveSuccess = true;
        setTimeout(() => { this.saveMessage = ''; }, 3000);
        // Refresh templates for the now-active provider.
        this.loadTemplates();
      } catch (err) {
        this.saveMessage = `Error: ${err.response?.data?.detail || err.message}`;
        this.saveSuccess = false;
      } finally {
        this.isActivating = false;
      }
    },
    async loadTemplates() {
      this.isLoadingTemplates = true;
      this.templatesError = '';
      try {
        const res = await axios.get(`${this.$API_URL}/whatsapp/integration/templates`, {
          headers: this.authHeaders()
        });
        this.templates = res.data.templates || [];
      } catch (err) {
        this.templatesError = err.response?.data?.detail || 'Failed to load templates.';
      } finally {
        this.isLoadingTemplates = false;
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      this.templatesError = '';
      try {
        const res = await axios.post(`${this.$API_URL}/whatsapp/integration/templates/sync`, {}, {
          headers: this.authHeaders()
        });
        this.templates = res.data.templates || [];
      } catch (err) {
        this.templatesError = err.response?.data?.detail || 'Failed to sync templates.';
      } finally {
        this.isSyncingTemplates = false;
      }
    },
    async loadTemplateUsage() {
      try {
        const res = await axios.get(`${this.$API_URL}/whatsapp/integration/template-usage`, {
          headers: this.authHeaders()
        });
        const byPurpose = {};
        (res.data.usage || []).forEach(u => { byPurpose[u.purpose] = u; });
        this.usageRows.forEach(row => {
          const u = byPurpose[row.purpose];
          if (u) {
            row.template_name = u.template_name || '';
            row.language = u.language || 'en';
          }
        });
      } catch (err) {
        // Non-fatal — mapping just stays blank.
        console.error('Failed to load template usage:', err);
      }
    },
    templatesForPurpose(purpose) {
      // OTP must use an AUTHENTICATION-category template — only those show in its
      // dropdown. Other purposes can use any synced template.
      if (purpose === 'otp') {
        return this.templates.filter(t => (t.category || '').toUpperCase() === 'AUTHENTICATION');
      }
      return this.templates;
    },
    onUsageChange(row) {
      // Keep language in sync with the picked template.
      const tpl = this.templates.find(t => t.name === row.template_name);
      row.language = (tpl && tpl.language) || 'en';
    },
    async saveTemplateUsage() {
      this.usageMessage = '';
      this.isSavingUsage = true;
      try {
        const items = this.usageRows.map(r => ({
          purpose: r.purpose,
          template_name: r.template_name || '',
          language: r.language || 'en'
        }));
        await axios.put(
          `${this.$API_URL}/whatsapp/integration/template-usage`,
          { items },
          { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
        );
        this.usageMessage = 'Template usage saved.';
        this.usageSuccess = true;
        setTimeout(() => { this.usageMessage = ''; }, 3000);
      } catch (err) {
        this.usageMessage = `Error: ${err.response?.data?.detail || err.message}`;
        this.usageSuccess = false;
      } finally {
        this.isSavingUsage = false;
      }
    },
    statusClass(status) {
      const s = (status || '').toUpperCase();
      if (s === 'APPROVED') return 'approved';
      if (s === 'REJECTED') return 'rejected';
      return 'pending';
    },
    formatDate(value) {
      if (!value) return '';
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },
    varLabel(n) {
      return '{{' + n + '}}';
    },
    openPreview(tpl) {
      this.previewTpl = tpl;
      this.showPreviewModal = true;
    },
    closePreview() {
      this.showPreviewModal = false;
      this.previewTpl = null;
    },
    openCreateTemplate() {
      this.createMessage = '';
      this.exampleValues = {};
      this.showCreateModal = true;
    },
    closeCreateTemplate() {
      this.showCreateModal = false;
    },
    async submitCreateTemplate() {
      this.createMessage = '';
      if (!this.createForm.template_name || !this.createForm.template_body) {
        this.createMessage = 'Template name and body are required.';
        this.createSuccess = false;
        return;
      }
      // Build example values in {{1}}, {{2}}... order from the per-variable inputs
      const vars = this.detectedVars;
      if (vars.length && vars.some(n => !(this.exampleValues[n] || '').trim())) {
        this.createMessage = 'Please fill a sample value for every variable.';
        this.createSuccess = false;
        return;
      }
      const exampleFields = vars.map(n => (this.exampleValues[n] || '').trim());

      const payload = {
        template_name: this.createForm.template_name.trim(),
        category: this.createForm.category,
        language_code: this.createForm.language_code.trim() || 'en_US',
        template_type: this.createForm.template_type.trim() || 'header',
        template_body: this.createForm.template_body,
        template_footer: this.createForm.template_footer || null,
        example_body_fields: exampleFields.length ? exampleFields : null
      };
      this.isCreating = true;
      try {
        const res = await axios.post(`${this.$API_URL}/whatsapp/integration/templates/create`, payload, {
          headers: { ...this.authHeaders(), 'Content-Type': 'application/json' }
        });
        this.templates = res.data.templates || this.templates;
        this.createMessage = res.data.message || 'Template submitted for review.';
        this.createSuccess = true;
        setTimeout(() => { this.showCreateModal = false; }, 1800);
      } catch (err) {
        this.createMessage = err.response?.data?.detail || err.message;
        this.createSuccess = false;
      } finally {
        this.isCreating = false;
      }
    },
    async save() {
      this.saveMessage = '';
      const businessApi = this.isBusinessApi;

      if (!this.form.api_base_url) {
        this.saveMessage = 'API Base URL is required.';
        this.saveSuccess = false;
        return;
      }
      if (!businessApi && !this.form.vendor_uid) {
        this.saveMessage = 'Vendor UID is required for the ApnaAd provider.';
        this.saveSuccess = false;
        return;
      }
      if (!this.existingConfig && !this.form.api_token) {
        this.saveMessage = `${businessApi ? 'API Key' : 'API Token'} is required when creating a new configuration.`;
        this.saveSuccess = false;
        return;
      }

      const payload = { ...this.form, provider_kind: this.selectedProvider };
      if (this.existingConfig && !payload.api_token) {
        delete payload.api_token;
      }

      this.isSaving = true;
      try {
        const res = await axios.put(
          `${this.$API_URL}/whatsapp/integration`,
          payload,
          { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
        );
        this.existingConfig = res.data;
        this.configs[this.selectedProvider] = res.data;
        if (res.data.is_active) this.activeProvider = this.selectedProvider;
        this.form.api_token = '';
        this.saveMessage = 'Configuration saved successfully.';
        this.saveSuccess = true;
        setTimeout(() => { this.saveMessage = ''; }, 3000);
      } catch (err) {
        const detail = err.response?.data?.detail || err.message;
        this.saveMessage = `Error: ${detail}`;
        this.saveSuccess = false;
      } finally {
        this.isSaving = false;
      }
    },
    openTest() {
      this.testMessage = '';
      this.showTestModal = true;
    },
    closeTest() {
      this.showTestModal = false;
    },
    async sendTest() {
      this.testMessage = '';
      if (!/^\d{10,15}$/.test(this.testForm.phone_number)) {
        this.testMessage = 'Enter a valid phone number (10-15 digits with country code).';
        this.testSuccess = false;
        return;
      }
      this.isTesting = true;
      try {
        await axios.post(
          `${this.$API_URL}/whatsapp/integration/test`,
          this.testForm,
          { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
        );
        this.testMessage = 'Test message sent successfully! Check your WhatsApp.';
        this.testSuccess = true;
      } catch (err) {
        const detail = err.response?.data?.detail || err.message;
        this.testMessage = `Error: ${detail}`;
        this.testSuccess = false;
      } finally {
        this.isTesting = false;
      }
    },
    openTestTemplate() {
      this.testTemplateMessage = '';
      this.testTemplateForm.template_name = '';
      this.testTemplateForm.template_language = 'en';
      this.showTestTemplateModal = true;
    },
    closeTestTemplate() {
      this.showTestTemplateModal = false;
    },
    onTestTemplateChange() {
      // Reset variable values and pick up the template's language.
      const tpl = this.selectedTestTemplate;
      Object.keys(this.testTemplateForm)
        .filter(k => k.startsWith('field_'))
        .forEach(k => { delete this.testTemplateForm[k]; });
      this.testTemplateForm.template_language = (tpl && tpl.language) || 'en';
      // Ensure reactive field_<n> keys exist for the detected vars.
      this.testTemplateVars.forEach(n => {
        this.testTemplateForm['field_' + n] = this.testTemplateForm['field_' + n] || '';
      });
    },
    renderTestPreview(text) {
      // Replace {{n}} with the entered value (or keep placeholder) for the preview.
      return (text || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (m, n) => {
        const v = this.testTemplateForm['field_' + n];
        return (v && v.trim()) ? v : `{{${n}}}`;
      });
    },
    async sendTestTemplate() {
      this.testTemplateMessage = '';
      if (!/^\d{10,15}$/.test(this.testTemplateForm.phone_number)) {
        this.testTemplateMessage = 'Enter a valid phone number (10-15 digits with country code).';
        this.testTemplateSuccess = false;
        return;
      }
      if (!this.testTemplateForm.template_name) {
        this.testTemplateMessage = 'Please choose a template.';
        this.testTemplateSuccess = false;
        return;
      }
      // All detected variables must be filled.
      const missing = this.testTemplateVars.some(n => !(this.testTemplateForm['field_' + n] || '').trim());
      if (missing) {
        this.testTemplateMessage = 'Please fill a value for every variable.';
        this.testTemplateSuccess = false;
        return;
      }
      // Build the fields dict (field_1..field_N) in variable order.
      const fields = {};
      this.testTemplateVars.forEach(n => { fields['field_' + n] = this.testTemplateForm['field_' + n]; });

      this.isTesting = true;
      try {
        await axios.post(
          `${this.$API_URL}/whatsapp/integration/test-template`,
          {
            phone_number: this.testTemplateForm.phone_number,
            template_name: this.testTemplateForm.template_name,
            template_language: this.testTemplateForm.template_language || 'en',
            fields
          },
          { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
        );
        this.testTemplateMessage = 'Template message sent successfully! Check your WhatsApp.';
        this.testTemplateSuccess = true;
      } catch (err) {
        const detail = err.response?.data?.detail || err.message;
        this.testTemplateMessage = `Error: ${detail}`;
        this.testTemplateSuccess = false;
      } finally {
        this.isTesting = false;
      }
    },
    openTestOtp() {
      this.testOtpMessage = '';
      this.generatedOtp = '';
      this.testOtpForm.phone_number = '';
      this.showTestOtpModal = true;
    },
    closeTestOtp() {
      this.showTestOtpModal = false;
    },
    async sendTestOtp() {
      this.testOtpMessage = '';
      this.generatedOtp = '';
      if (!/^\d{10,15}$/.test(this.testOtpForm.phone_number)) {
        this.testOtpMessage = 'Enter a valid phone number (10-15 digits with country code).';
        this.testOtpSuccess = false;
        return;
      }
      this.isTesting = true;
      try {
        const res = await axios.post(
          `${this.$API_URL}/whatsapp/integration/test-otp`,
          this.testOtpForm,
          { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
        );
        this.generatedOtp = res.data.otp;
        this.testOtpMessage = 'Test OTP sent successfully! Check your WhatsApp.';
        this.testOtpSuccess = true;
      } catch (err) {
        const detail = err.response?.data?.detail || err.message;
        this.testOtpMessage = `Error: ${detail}`;
        this.testOtpSuccess = false;
      } finally {
        this.isTesting = false;
      }
    }
  },
  mounted() {
    this.loadConfig();
    this.loadTemplates();
    this.loadTemplateUsage();
  }
};
</script>

<style scoped>
.integrations-page {
  font-family: var(--font-sans);
  color: var(--text-primary);
}

/* ── Breadcrumb ── */
.breadcrumb-ig {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--fs-sm);
}
.breadcrumb-ig a { color: var(--text-muted); text-decoration: none; font-weight: 500; }
.breadcrumb-ig a:hover { color: var(--primary-600); }
.crumb-sep { color: var(--gray-300); }
.crumb-current { color: var(--primary-600); font-weight: 600; }
.crumb-link { color: var(--text-muted); font-weight: 500; cursor: pointer; }
.crumb-link:hover { color: var(--primary-600); }

/* ══════════════ LANDING HERO ══════════════ */
.ig-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-bottom: 1.75rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--gray-200);
}
.ig-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-h1);
  line-height: var(--lh-h1);
  font-weight: var(--fw-h1);
  letter-spacing: -0.02em;
  color: var(--text-primary);
}
.ig-subtitle {
  margin: var(--space-heading-body) 0 0;
  max-width: 620px;
  color: var(--text-secondary);
  font-size: var(--fs-body);
  line-height: var(--lh-body);
}
.ig-hero-stat {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 14px;
}
.stat-num {
  font-family: var(--font-display);
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1;
  color: var(--primary-600);
}
.stat-lbl {
  margin-top: 4px;
  font-size: var(--fs-caption);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
}

/* ══════════════ CATALOG ══════════════ */
.catalog-section { margin-bottom: 2.5rem; }
.catalog-head {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}
.catalog-title {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-label);
}
.catalog-count { font-size: var(--fs-caption); color: var(--text-muted); font-weight: 500; }

.catalog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.app-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid var(--gray-200);
  border-radius: 16px;
  padding: 1.5rem;
  cursor: pointer;
  transition: transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .22s, border-color .22s;
}
.app-card:hover,
.app-card:focus-visible {
  transform: translateY(-3px);
  box-shadow: 0 18px 36px -18px rgba(11,18,32,.28);
  border-color: var(--gray-300);
  outline: none;
}
.app-card:hover .app-open { color: #1fa855; }
.app-card:hover .app-open svg { transform: translateX(3px); }

.app-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.1rem;
}
.app-logo {
  width: 54px;
  height: 54px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.app-logo-wa {
  background: linear-gradient(135deg, #25d366 0%, #12a150 100%);
  box-shadow: 0 8px 18px -6px rgba(18,161,80,.55);
}
.app-logo-email { background: linear-gradient(135deg, #0084FF 0%, #0071C5 100%); }
.app-logo-sms   { background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%); }
.app-logo-slack { background: linear-gradient(135deg, #4A154B 0%, #611f69 100%); }

/* Coming-soon cards — muted, non-interactive */
.app-card-soon {
  cursor: default;
  background: var(--gray-50);
}
.app-card-soon:hover { transform: none; box-shadow: none; border-color: var(--gray-200); }
.app-card-soon .app-logo { opacity: .85; box-shadow: none; }
.app-card-soon .app-name,
.app-card-soon .app-desc { opacity: .8; }
.conn-soon {
  background: var(--gray-100);
  color: var(--gray-500);
  font-weight: 600;
}
.app-open-muted { color: var(--gray-400); font-weight: 600; }
.app-name {
  margin: 0 0 0.4rem;
  font-size: 1.08rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--text-primary);
}
.app-desc {
  margin: 0;
  flex: 1;
  font-size: var(--fs-sm);
  line-height: 1.55;
  color: var(--text-secondary);
}
.app-card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.35rem;
  padding-top: 1rem;
  border-top: 1px solid var(--gray-100);
}
.app-provider {
  font-size: var(--fs-caption);
  color: var(--text-muted);
  font-weight: 500;
}
.app-open {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.83rem;
  font-weight: 700;
  color: var(--text-label);
  transition: color .2s;
}
.app-open svg { transition: transform .2s; }

/* ══════════════ CONNECTION PILL ══════════════ */
.conn-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 11px 4px 9px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.conn-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.conn-on   { background: #ecfdf3; color: #067647; }
.conn-warn { background: #fffaeb; color: #b54708; }
.conn-off  { background: var(--gray-100); color: var(--gray-600); }

/* ══════════════ DETAIL HERO ══════════════ */
.detail-hero {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1.5rem;
  margin-bottom: 1.75rem;
  border-bottom: 1px solid var(--gray-200);
}
.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  border: 1px solid var(--gray-200);
  background: #fff;
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;
  transition: all .15s;
}
.back-btn:hover { background: var(--gray-50); border-color: var(--gray-300); color: var(--text-primary); }
.detail-logo { width: 48px; height: 48px; border-radius: 13px; }
.detail-hero-text { min-width: 0; }
.detail-title-row { display: flex; align-items: center; gap: 0.75rem; }
.detail-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-primary);
}
.detail-sub {
  margin: 0.3rem 0 0;
  font-size: var(--fs-sm);
  color: var(--text-secondary);
}

.detail-hero-action {
  margin-left: auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1rem;
  border: 1px solid var(--gray-200);
  border-radius: 10px;
  background: #fff;
  color: var(--text-label);
  font-size: 0.83rem;
  font-weight: 600;
  text-decoration: none;
  transition: all .15s;
  flex-shrink: 0;
}
.detail-hero-action:hover { background: var(--gray-50); border-color: var(--gray-300); color: var(--text-primary); }

/* ══════════════ TABS ══════════════ */
.tab-bar {
  display: flex;
  gap: 0.25rem;
  border-bottom: 1px solid var(--gray-200);
  margin-bottom: 1.75rem;
}
.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.15rem;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  position: relative;
  transition: color .15s;
}
.tab-btn svg { opacity: .8; }
.tab-btn:hover { color: var(--text-primary); }
.tab-btn.active { color: var(--primary-600); }
.tab-btn.active::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: -1px;
  height: 2px;
  background: var(--primary-600);
  border-radius: 2px 2px 0 0;
}
.tab-count {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--gray-100);
  color: var(--gray-600);
}
.tab-btn.active .tab-count { background: var(--blue-50); color: var(--primary-600); }
.tab-panel { min-width: 0; }

/* ── Config tab: form + guide two-column ── */
.config-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 1.5rem;
  align-items: start;
}
.config-main { margin-bottom: 0; min-width: 0; }
.config-guide {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 1.5rem;
}
@media (max-width: 1024px) {
  .config-layout { grid-template-columns: 1fr; }
  .config-guide { position: static; }
}

/* ── Provider switcher ── */
.provider-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;
}
.provider-tab {
  border: 1.5px solid var(--gray-200);
  border-radius: 14px;
  padding: 1rem 1.15rem;
  background: #fff;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s, background .15s;
}
.provider-tab:hover { border-color: var(--gray-300); }
.provider-tab.sel {
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px rgba(0,132,255,.10);
}
.provider-tab-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.provider-role {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-muted);
}
.provider-onoff {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--gray-100);
  color: var(--gray-500);
}
.provider-onoff.on { background: #ecfdf3; color: #067647; }
.provider-name { margin: 0 0 0.2rem; font-size: 0.98rem; font-weight: 700; color: var(--text-primary); }
.provider-desc { margin: 0; font-size: 0.78rem; color: var(--text-muted); }

/* ── Activate banner ── */
.activate-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--gray-200);
  border-radius: 12px;
  background: var(--gray-50);
}
.activate-row.active { background: #f0fdf7; border-color: #a6f4c5; }
.activate-text { display: flex; flex-direction: column; gap: 2px; }
.activate-text strong { font-size: 0.88rem; font-weight: 700; color: var(--text-primary); }
.activate-text span { font-size: 0.79rem; color: var(--text-muted); }
.btn-activate {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.25rem;
  border: none;
  border-radius: 9px;
  background: var(--primary-600);
  color: #fff;
  font-family: inherit;
  font-size: 0.83rem;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, opacity .15s;
}
.btn-activate:hover:not(:disabled) { background: var(--primary-700); }
.btn-activate:disabled { opacity: 0.55; cursor: not-allowed; }
.activate-row.active .btn-activate { background: #12a150; }

@media (max-width: 560px) {
  .provider-switch { grid-template-columns: 1fr; }
}

.side-card {
  background: #fff;
  border: 1px solid var(--gray-200);
  border-radius: 14px;
  padding: 1.35rem;
}
.side-title {
  margin: 0 0 0.85rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-label);
}
.side-status { margin-bottom: 0.9rem; }
.side-note {
  margin: 0 0 1rem;
  font-size: 0.8rem;
  line-height: 1.55;
  color: var(--text-muted);
}
.side-note:last-child { margin-bottom: 0; }
.side-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--primary-600);
  text-decoration: none;
}
.side-link:hover { text-decoration: underline; }
.side-btn {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.7rem 0.95rem;
  margin-bottom: 0.55rem;
  border: 1px solid var(--gray-200);
  border-radius: 10px;
  background: #fff;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
  text-align: left;
}
.side-btn:last-child { margin-bottom: 0; }
.side-btn:hover:not(:disabled) { background: var(--gray-50); border-color: var(--gray-300); }
.side-btn:disabled { opacity: .5; cursor: not-allowed; }
.side-btn svg { color: #12a150; flex-shrink: 0; }

/* ══════════════ CARD (form container) ══════════════ */
.integration-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid var(--gray-200);
  box-shadow: 0 1px 2px rgba(11,18,32,.04);
  margin-bottom: 1.5rem;
  overflow: hidden;
}
.card-header {
  padding: 1.15rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--gray-50);
}
.card-title-row { display: flex; align-items: center; gap: 0.85rem; }
.card-icon {
  width: 38px; height: 38px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.card-title { margin: 0; font-size: 1rem; font-weight: 700; color: var(--text-primary); }
.card-subtitle { margin: 0.2rem 0 0 0; font-size: 0.8rem; color: var(--text-muted); }

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.status-badge.success { background: #ecfdf5; color: #10b981; }
.status-badge.warning { background: #fef3c7; color: #92400e; }
.status-badge.neutral { background: var(--gray-100); color: var(--gray-600); }

.card-body { padding: 1.5rem; }

.section { margin-bottom: 1.5rem; }
.section:last-child { margin-bottom: 0; }
.section-title {
  margin: 0 0 0.9rem 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.form-field-full { grid-column: 1 / -1; }
@media (max-width: 720px) {
  .form-grid { grid-template-columns: 1fr; }
  .form-field-full { grid-column: auto; }
}

.form-field { display: flex; flex-direction: column; }
.form-label {
  margin-bottom: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
}
.req { color: #ef4444; margin-left: 2px; }

.form-input {
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.875rem;
  color: #0f172a;
  transition: all 0.15s;
  box-sizing: border-box;
  background: white;
}
.form-input:focus {
  outline: none;
  border-color: #0071C5;
  box-shadow: 0 0 0 3px rgba(0, 113, 197, 0.1);
}
.form-hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.72rem;
  color: #94a3b8;
}
.form-hint code {
  background: #f1f5f9;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.7rem;
  color: #475569;
}

.token-wrapper { position: relative; }
.token-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 0.25rem;
  display: flex;
}
.token-toggle:hover { color: #0f172a; }

.advanced-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  padding: 0.5rem 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  font-family: inherit;
}
.advanced-toggle:hover { color: #0f172a; }
.advanced-content {
  margin-top: 0.75rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.toggle-switch {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
}
.toggle-switch input { display: none; }
.toggle-slider {
  position: relative;
  width: 40px;
  height: 22px;
  background: #cbd5e1;
  border-radius: 999px;
  transition: background 0.2s;
}
.toggle-slider::after {
  content: '';
  position: absolute;
  left: 2px;
  top: 2px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}
.toggle-switch input:checked + .toggle-slider { background: #25d366; }
.toggle-switch input:checked + .toggle-slider::after { transform: translateX(18px); }
.toggle-label { font-size: 0.85rem; font-weight: 600; color: #0f172a; }

.alert {
  padding: 0.8rem 1rem;
  border-radius: 10px;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}
.alert-success { background: #ecfdf5; border: 1px solid #86efac; color: #166534; }
.alert-error { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }

.actions-row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding-top: 0.5rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  background: #0071C5;
  color: white;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  font-size: 0.85rem;
  box-shadow: 0 4px 12px rgba(0, 113, 197, 0.2);
}
.btn-primary.whatsapp {
  background: linear-gradient(135deg, #25d366 0%, #1fa85c 100%);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.25);
}
.btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0, 113, 197, 0.3); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: white;
  color: #0f172a;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  font-size: 0.85rem;
}
.btn-secondary:hover:not(:disabled) { background: #f8fafc; border-color: #94a3b8; }
.btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }

.placeholder-box {
  padding: 1.5rem;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  text-align: center;
}
.placeholder-box p { margin: 0.25rem 0; color: #475569; font-size: 0.875rem; }
.placeholder-box .muted { color: #94a3b8; font-size: 0.8rem; }

.loading-state {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}
.spinner-lg {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #0071C5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 0.75rem;
}
.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-backdrop { position: absolute; width: 100%; height: 100%; cursor: pointer; }
.modal-panel {
  position: relative;
  z-index: 10;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  width: 90%;
  max-width: 440px;
}
.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex; align-items: center; justify-content: space-between;
}
.modal-title { margin: 0; font-size: 1.05rem; font-weight: 700; color: #0f172a; }
.modal-close-btn {
  background: none; border: none; cursor: pointer;
  color: #64748b; padding: 0.25rem; display: flex;
}
.modal-close-btn:hover { color: #0f172a; }
.modal-body { padding: 1.5rem; }
.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

/* Template list */
.template-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.template-item {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.15rem;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.template-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 6px -1px rgba(0,0,0,0.06);
}
/* Header actions */
.card-header-actions {
  display: flex;
  gap: 0.6rem;
  flex-shrink: 0;
  align-items: center;
}
.btn-outline, .btn-solid {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 1rem;
  border-radius: 9px;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  line-height: 1;
}
.btn-outline {
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #334155;
  text-decoration: none;
}
.btn-outline:hover:not(:disabled) { background: #f8fafc; border-color: #94a3b8; }
.btn-solid {
  background: #0071C5;
  border: 1px solid #0071C5;
  color: #fff;
  box-shadow: 0 2px 6px rgba(0,113,197,0.25);
}
.btn-solid:hover:not(:disabled) { background: #005ba3; }
.btn-outline:disabled, .btn-solid:disabled { opacity: 0.5; cursor: not-allowed; }

/* Toolbar above table */
.templates-toolbar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
}
.count-pill {
  font-size: 0.72rem;
  font-weight: 700;
  color: #0071C5;
  background: #eff6ff;
  padding: 1px 9px;
  border-radius: 20px;
}
.toolbar-hint {
  margin-left: auto;
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Templates table */
.tpl-table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.tpl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.tpl-table thead th {
  text-align: left;
  padding: 0.7rem 1rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.tpl-row {
  cursor: pointer;
  transition: background 0.12s;
}
.tpl-row:not(:last-child) td { border-bottom: 1px solid #f1f5f9; }
.tpl-row:hover { background: #f8fafc; }
.tpl-table td {
  padding: 0.8rem 1rem;
  color: #475569;
  vertical-align: middle;
}
.td-name code {
  font-family: ui-monospace, monospace;
  font-weight: 700;
  color: #0f172a;
  font-size: 0.82rem;
}
.td-date { color: #94a3b8; font-size: 0.8rem; white-space: nowrap; }

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 3px 9px;
  border-radius: 20px;
}
.status-pill.approved { background: #ecfdf5; color: #059669; }
.status-pill.pending  { background: #fef3c7; color: #b45309; }
.status-pill.rejected { background: #fef2f2; color: #dc2626; }

.meta-chip {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 2px 9px;
  border-radius: 6px;
}

/* Preview modal */
.preview-meta-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
}
.preview-name {
  font-family: ui-monospace, monospace;
  font-weight: 700;
  color: #0f172a;
  font-size: 0.9rem;
}
.preview-meta-tags {
  display: flex;
  gap: 0.4rem;
  margin-bottom: 1.2rem;
}
.wa-preview {
  background: #e5ddd5;
  background-image: linear-gradient(rgba(229,221,213,0.6), rgba(229,221,213,0.6));
  border-radius: 12px;
  padding: 1.25rem;
}
.wa-bubble {
  position: relative;
  background: #fff;
  border-radius: 8px 8px 8px 0;
  padding: 0.6rem 0.7rem 0.45rem;
  max-width: 85%;
  box-shadow: 0 1px 1px rgba(0,0,0,0.12);
}
.wa-header { font-weight: 700; color: #111b21; font-size: 0.88rem; margin-bottom: 0.35rem; }
.wa-body { color: #111b21; font-size: 0.86rem; line-height: 1.5; white-space: pre-wrap; }
.wa-body.wa-empty { color: #8696a0; font-style: italic; }
.wa-footer { color: #8696a0; font-size: 0.78rem; margin-top: 0.4rem; }
.wa-time { text-align: right; color: #8696a0; font-size: 0.65rem; margin-top: 0.25rem; }
.wa-buttons {
  margin-top: 0.5rem;
  max-width: 85%;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.wa-btn {
  background: #fff;
  color: #00a5f4;
  font-size: 0.84rem;
  font-weight: 500;
  text-align: center;
  padding: 0.55rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  box-shadow: 0 1px 1px rgba(0,0,0,0.12);
}

.spinner-sm.dark {
  border: 2px solid rgba(0,0,0,0.15);
  border-top-color: #0071C5;
}
.modal-wide { max-width: 560px; }

/* Per-variable example inputs */
.var-grid {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
.var-field {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.var-tag {
  flex-shrink: 0;
  min-width: 46px;
  text-align: center;
  font-family: ui-monospace, monospace;
  font-size: 0.78rem;
  font-weight: 700;
  color: #0071C5;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  padding: 6px 8px;
  border-radius: 8px;
}
.var-field .form-input { margin: 0; }

/* Template usage mapping */
.usage-grid {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}
.usage-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.usage-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 200px;
  flex-shrink: 0;
}
.usage-label strong { font-size: 0.88rem; font-weight: 600; color: var(--text-primary, #0f172a); }
.usage-label span { font-size: 0.76rem; color: var(--text-muted, #94a3b8); }
.usage-select { flex: 1; }
@media (max-width: 640px) {
  .usage-row { flex-direction: column; align-items: stretch; gap: 0.4rem; }
  .usage-label { min-width: 0; }
}
</style>
